
import { mapState, mapGetters } from 'vuex';
import trimMessageForReward from '@/lib/trimMessageForReward';
import ReviewContentType from '@/enums/ReviewContentType';
import ReviewPointType from '@/enums/ReviewPointType';
import ReviewScore from '@/enums/ReviewScore';
import HttpStatus from '@/enums/HttpStatus';
import InsufficientReviewLengthActionType from '@/enums/InsufficientReviewLengthActionType';
import RewardInfo from './RewardInfo';
import MediaField from './MediaField';
import OptionField from './OptionField';

export default {
  i18nComponentKey: 'ReviewsEditLegacy.components.ReviewForm',
  name: 'ReviewForm',
  components: {
    RewardInfo,
    MediaField,
    OptionField
  },
  props: {
    review: { type: Object, required: true },
    productId: { type: Number, default: null },
    subOrderId: { type: Number, default: null },
    password: { type: String, default: '' },
    formEventBus: { type: Object, required: true }
  },
  data() {
    return {
      formObject: null,
      isSubmitting: false,
      hasError: false
    };
  },
  computed: {
    ...mapState(['api']),
    ...mapState('reviewSettings', ['reviewSettings']),
    ...mapState('window', ['windowService']),
    ...mapGetters('reviewSettings', ['isVideoUploadable']),
    isSubmittable() {
      return !this.hasError && !this.isSubmitting;
    },
    fieldKeys() {
      return [
        this.reviewSettings.popup_reward_detail ? 'reward' : null,
        this.review.product_options.length ? 'product_options' : null,
        !this.review.brand_user_id ? 'user_name' : null,
        !this.review.brand_user_id && !this.review.id ? 'password' : null,
        'message',
        'media',
        'score',
        ...this.optionTypes.map(e => e.key)
      ].filter(v => v);
    },
    fieldOptionsMap() {
      const map = {
        reward: { type: 'custom' },
        user_name: {
          label: this.$t('user_name.label'),
          type: 'text',
          inputProps: {
            maxlength: 32,
            placeholder: this.$t('user_name.placeholder')
          }
        },
        password: {
          label: this.$t('password.label'),
          type: 'password',
          inputProps: {
            maxlength: 32,
            placeholder: this.$t('password.placeholder')
          }
        },
        product_options: {
          label: this.$t('product_options.label'),
          type: 'text',
          inputProps: {
            readonly: true,
            name: ''
          }
        },
        message: {
          label: this.$t('message.label'),
          subLabel:
            this.review.content_type === ReviewContentType.RESTRICTED
              ? this.$t('message.sub_label_restricted')
              : '',
          type: 'textarea',
          inputProps: {
            rows: 3,
            maxlength: 2000,
            placeholder: this.review.message_default
          }
        },
        media: {
          label: this.$t(
            this.isVideoUploadable ? 'media.label_media' : 'media.label_image'
          ),
          subLabel: [
            this.review.content_type === ReviewContentType.RESTRICTED
              ? this.$t('media.sub_label_restricted')
              : null,
            this.$t('media.sub_label_description')
          ]
            .filter(v => v)
            .join('\n'),
          type: 'custom',
          validators: [
            {
              rule: val =>
                val.filter(v => v.type === 'video').every(v => !v.uploading),
              message: this.$t('media.video_uploading')
            }
          ]
        },
        score: {
          label: this.$t('score.label'),
          type: 'select',
          inputProps: {
            options: ReviewScore.values.map(value => ({
              label: `${'★'.repeat(value)}${'☆'.repeat(
                5 - value
              )} ${ReviewScore.t(value)}`,
              value
            }))
          }
        },
        ...this.optionFieldOptionsMap
      };

      this.optionTypes.forEach(optionType => {
        map[optionType.key] = {
          label: optionType.name,
          type: 'custom',
          required: optionType.required
        };
      });

      return map;
    },
    fields() {
      return this.fieldKeys.map(key => ({ key, ...this.fieldOptionsMap[key] }));
    },
    optionTypes() {
      return this.review.option_types.map(t => ({
        ...t,
        key: `option_${t.id}`
      }));
    },
    isMessageEligibleForReward() {
      const {
        auto_mileage_use_valid_review_message_length,
        min_review_length_to_get_mileage
      } = this.reviewSettings;

      const messageLength = auto_mileage_use_valid_review_message_length
        ? trimMessageForReward(this.formObject.message).length
        : this.formObject.message.length;

      return min_review_length_to_get_mileage <= messageLength;
    },
    messageLengthConfirmMessage() {
      const {
        auto_mileage_use_valid_review_message_length,
        min_review_length_to_get_mileage,
        review_point_type
      } = this.reviewSettings;

      let key;
      if (review_point_type === ReviewPointType.DISCOUNT_CODE)
        key = auto_mileage_use_valid_review_message_length
          ? 'message_length_confirm.discount_code_eligible_message'
          : 'message_length_confirm.discount_code_full_message';
      else
        key = auto_mileage_use_valid_review_message_length
          ? 'message_length_confirm.mileage_eligible_message'
          : 'message_length_confirm.mileage_full_message';

      return this.$t(key, { length: min_review_length_to_get_mileage });
    },
    messageLengthAlertMessage() {
      const {
        auto_mileage_use_valid_review_message_length,
        min_review_length_to_get_mileage
      } = this.reviewSettings;

      const key = auto_mileage_use_valid_review_message_length
        ? 'message_length_alert.eligible_message'
        : 'message_length_alert.full_message';
      return this.$t(key, { length: min_review_length_to_get_mileage });
    }
  },
  watch: {
    isSubmittable(value) {
      this.$emit('submittable', value);
    },
    review() {
      this.initFormObject();
    }
  },
  created() {
    this.initFormObject();
  },
  methods: {
    initFormObject() {
      const review = this.review;
      this.formObject = {
        product_options: review.product_options.map(o => o.value).join(', '),
        message: review.message,
        score: review.score,
        ...(review.brand_user_id
          ? {}
          : { user_name: review.user_name, password: '' }),
        options: review.options.map(o => ({
          key: `option_${o.review_option_type_id}`,
          id: o.id,
          value: o.value
        })),
        media: [
          ...review.videos.map(e => ({
            type: 'video',
            thumbnail_url: e.thumbnail_url,
            id: e.id,
            uploading: false
          })),
          ...review.images.map(e => ({
            type: 'image',
            thumbnail_url: e.thumbnail_url,
            url: e.url
          }))
        ]
      };
    },
    onSubmit(formData) {
      this.checkMessageLength()
        .then(() => this.submit(formData))
        .catch(() => {});
    },
    checkMessageLength() {
      return new Promise((resolve, reject) => {
        const actionType = this.reviewSettings
          .action_for_insufficient_review_length_to_get_mileage;
        if (
          actionType === InsufficientReviewLengthActionType.DO_NOTHING ||
          this.isMessageEligibleForReward
        ) {
          resolve();
          return;
        }

        if (actionType === InsufficientReviewLengthActionType.SHOW_ALERT) {
          this.windowService
            .confirm(this.messageLengthConfirmMessage)
            .then(response => (response ? resolve() : reject()));
        } else {
          this.windowService.alert(this.messageLengthAlertMessage);
          reject();
        }
      });
    },
    submit(formData) {
      this.isSubmitting = true;
      const promise = this.review.id
        ? this.api.updateReview(this.review.id, formData)
        : this.api.createReview(this.productId, this.subOrderId, formData);
      promise
        .then(() => {
          this.windowService.alert(this.$t('app.saved'));
          this.$emit('updated');
        })
        .catch(error => {
          if (error.response.status === HttpStatus.UNPROCESSABLE_ENTITY) {
            const message = error.response.data.errors
              .map(e =>
                this.$t(
                  `review_error.${e.attribute}.${e.error}`,
                  e.error === 'profanity' ? [e.profanities.join(', ')] : e
                )
              )
              .join('\n');
            this.windowService.alert(message);
          } else throw error;
        })
        .finally(() => (this.isSubmitting = false));
    },
    optionType(key) {
      return this.optionTypes.find(t => t.key === key);
    },
    option(key) {
      const option = this.formObject.options.find(o => o.key === key);
      return option
        ? { id: option.id, value: option.value }
        : { id: null, value: '' };
    },
    onChangeOptionValue(key, value) {
      this.formObject = {
        ...this.formObject,
        options: this.formObject.options.map(option =>
          option.key === key ? { ...option, value } : option
        )
      };
    }
  }
};
